/**
 * "For You" / Personalized Home — lets a signed-in reader pin sections
 * and locations, and shows their personalized feed alongside the rest.
 *
 * When the reader isn't signed in, we render the same feed as
 * `NewsPulseHome` sees anonymously, plus a sign-in nudge.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Pin, PinOff, Plus, X, MapPin, Loader2, Sparkles, GripVertical } from "lucide-react";
import { npApi, hasNpAuthToken } from "./api";
import { ALL_TOPICS } from "./theme";
import EventCard from "./EventCard";

// Flatten ALL_TOPICS into (slug, label) pairs whose slug is a real
// taxonomy section (i.e. `/topic/<slug>`).
const PINNABLE_SECTIONS = ALL_TOPICS.flatMap((g) =>
  g.items
    .filter(([, path]) => path.startsWith("/topic/"))
    .map(([label, path]) => ({ slug: path.replace("/topic/", ""), label, group: g.group })),
);

// Deduplicate — same slug can appear in multiple groups.
const UNIQUE_SECTIONS = Array.from(
  new Map(PINNABLE_SECTIONS.map((s) => [s.slug, s])).values(),
);

const SECTIONS_BY_SLUG = Object.fromEntries(
  UNIQUE_SECTIONS.map((s) => [s.slug, s]),
);

/**
 * Reorderable list of pinned-section chips (native HTML5 drag).
 * `onReorder` fires with the new slug array on every drop; the parent
 * persists to the backend.
 */
function PinnedSectionList({ slugs, sectionsBySlug, onReorder, onRemove }) {
  const dragIndex = useRef(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleDragStart = (i) => (e) => {
    dragIndex.current = i;
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs data set to start the drag.
    try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) {}
  };
  const handleDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIndex !== i) setOverIndex(i);
  };
  const handleDragEnd = () => {
    dragIndex.current = null;
    setOverIndex(null);
  };
  const handleDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    dragIndex.current = null;
    setOverIndex(null);
    if (from === null || from === i) return;
    const next = [...slugs];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    onReorder(next);
  };

  if (!slugs.length) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="np-pinned-sections-list">
      {slugs.map((slug, i) => {
        const meta = sectionsBySlug[slug];
        if (!meta) return null;
        const isOver = overIndex === i;
        return (
          <div
            key={slug}
            draggable
            onDragStart={handleDragStart(i)}
            onDragOver={handleDragOver(i)}
            onDragEnd={handleDragEnd}
            onDrop={handleDrop(i)}
            className={`inline-flex items-center gap-1 rounded-full pl-1 pr-2 py-1 text-xs font-semibold border transition-colors cursor-move select-none ${
              isOver
                ? "bg-blue-700 text-white border-blue-800 shadow-md"
                : "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
            }`}
            data-testid={`np-pinned-chip-${slug}`}
            aria-grabbed={dragIndex.current === i}
          >
            <GripVertical className="w-3 h-3 opacity-70" />
            <Pin className="w-3 h-3" />
            <span>{meta.label}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemove(slug); }}
              aria-label={`Remove ${meta.label}`}
              className="ml-0.5 rounded-full hover:bg-white/20 w-4 h-4 flex items-center justify-center"
              data-testid={`np-unpin-${slug}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function hasAuthToken() {
  return hasNpAuthToken();
}

export default function NewsPulseForYou() {
  const [signedIn] = useState(hasAuthToken);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pinnedSections, setPinnedSections] = useState([]);
  const [pinnedLocations, setPinnedLocations] = useState([]);
  const [locationDraft, setLocationDraft] = useState("");
  const [pinnedCards, setPinnedCards] = useState([]);
  const [otherCards, setOtherCards] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (signedIn) {
        try {
          const prefs = await npApi.prefsGet();
          if (prefs) {
            setPinnedSections(prefs.pinned_sections || []);
            setPinnedLocations(prefs.pinned_locations || []);
          }
        } catch {
          /* prefs may not exist yet — fine */
        }
      }
      const feed = await npApi.forYouFeed({ limit_pinned: 20, limit_other: 15 });
      setPinnedCards(feed.pinned || []);
      setOtherCards(feed.other || []);
    } catch (e) {
      setError(e.message || "Could not load your feed");
    } finally {
      setLoading(false);
    }
  }, [signedIn]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const save = async (nextSections, nextLocations) => {
    if (!signedIn) return;
    setSaving(true);
    try {
      await npApi.prefsPut({
        pinned_sections: nextSections,
        pinned_locations: nextLocations,
      });
      const feed = await npApi.forYouFeed({ limit_pinned: 20, limit_other: 15 });
      setPinnedCards(feed.pinned || []);
      setOtherCards(feed.other || []);
    } catch (e) {
      setError(e.message || "Could not save your pins");
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (slug) => {
    const next = pinnedSections.includes(slug)
      ? pinnedSections.filter((s) => s !== slug)
      : [...pinnedSections, slug].slice(0, 20);
    setPinnedSections(next);
    save(next, pinnedLocations);
  };

  const addLocation = (e) => {
    e.preventDefault();
    const v = locationDraft.trim();
    if (!v || pinnedLocations.includes(v)) return;
    const next = [...pinnedLocations, v].slice(0, 15);
    setPinnedLocations(next);
    setLocationDraft("");
    save(pinnedSections, next);
  };

  const removeLocation = (loc) => {
    const next = pinnedLocations.filter((l) => l !== loc);
    setPinnedLocations(next);
    save(pinnedSections, next);
  };

  const grouped = useMemo(() => {
    const byGroup = new Map();
    UNIQUE_SECTIONS.forEach((s) => {
      if (!byGroup.has(s.group)) byGroup.set(s.group, []);
      byGroup.get(s.group).push(s);
    });
    return Array.from(byGroup.entries());
  }, []);

  const sectionsBySlug = SECTIONS_BY_SLUG;

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-8" data-testid="np-for-you-page">
      <header className="mb-6 flex items-center gap-3">
        <Sparkles className="w-6 h-6 text-blue-600" />
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            For You
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Personalized headlines from the sections and places you care about.
          </p>
        </div>
      </header>

      {/* Pin controls (only when signed in) */}
      {signedIn ? (
        <section
          className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mb-8"
          data-testid="np-for-you-pins"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-900">Your pins</h2>
            {saving && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
          </div>

          <div className="mb-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">
              Pinned sections
              {pinnedSections.length > 0 && (
                <span className="normal-case font-normal text-slate-400 ml-2">
                  drag to reorder
                </span>
              )}
            </div>
            <PinnedSectionList
              slugs={pinnedSections}
              sectionsBySlug={sectionsBySlug}
              onReorder={(next) => {
                setPinnedSections(next);
                save(next, pinnedLocations);
              }}
              onRemove={(slug) => toggleSection(slug)}
            />
            {pinnedSections.length === 0 && (
              <div className="text-xs text-slate-500 italic">
                Add sections below to build your personal feed.
              </div>
            )}

            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2 mt-4">
              Available sections
            </div>
            <div className="flex flex-wrap gap-3">
              {grouped.map(([group, items]) => (
                <fieldset key={group} className="min-w-0">
                  <legend className="text-[10px] uppercase text-slate-400 tracking-widest mb-1.5">
                    {group}
                  </legend>
                  <div className="flex flex-wrap gap-1.5">
                    {items
                      .filter((s) => !pinnedSections.includes(s.slug))
                      .map((s) => (
                        <button
                          key={s.slug}
                          type="button"
                          onClick={() => toggleSection(s.slug)}
                          className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold border bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:text-blue-700 transition-colors"
                          data-testid={`np-pin-section-${s.slug}`}
                          aria-pressed={false}
                        >
                          <PinOff className="w-3 h-3" />
                          {s.label}
                        </button>
                      ))}
                  </div>
                </fieldset>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">
              Locations
            </div>
            <form
              onSubmit={addLocation}
              className="flex items-center gap-2 mb-2"
              data-testid="np-add-location-form"
            >
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1.5 flex-1 max-w-md">
                <MapPin className="w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  value={locationDraft}
                  onChange={(e) => setLocationDraft(e.target.value)}
                  placeholder="Add a city, region or neighborhood…"
                  className="flex-1 bg-transparent border-0 outline-none text-sm"
                  data-testid="np-add-location-input"
                  maxLength={60}
                />
              </div>
              <button
                type="submit"
                disabled={!locationDraft.trim()}
                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white disabled:opacity-40 hover:bg-blue-700 transition-colors"
                data-testid="np-add-location-submit"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </form>
            <div className="flex flex-wrap gap-1.5">
              {pinnedLocations.length === 0 && (
                <span className="text-xs text-slate-500">
                  No locations pinned yet — try adding “Kerala”, “Berlin” or “Brooklyn”.
                </span>
              )}
              {pinnedLocations.map((loc) => (
                <span
                  key={loc}
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"
                  data-testid={`np-location-chip-${loc}`}
                >
                  <MapPin className="w-3 h-3" /> {loc}
                  <button
                    type="button"
                    onClick={() => removeLocation(loc)}
                    aria-label={`Remove ${loc}`}
                    className="ml-0.5 hover:text-emerald-950"
                    data-testid={`np-remove-location-${loc}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <div
          className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-8 flex flex-wrap items-center gap-3"
          data-testid="np-for-you-signin-nudge"
        >
          <Sparkles className="w-5 h-5 text-blue-600" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900">
              Sign in to personalize your feed
            </div>
            <div className="text-sm text-slate-600">
              Pin sections and neighborhoods so your top headlines match what matters to you.
            </div>
          </div>
          <Link
            to="/login"
            className="rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 transition-colors"
            data-testid="np-for-you-signin-cta"
          >
            Sign in
          </Link>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {/* Feeds */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading your feed…
        </div>
      ) : (
        <>
          {pinnedCards.length > 0 && (
            <section className="mb-10" data-testid="np-for-you-pinned-feed">
              <h2 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Pin className="w-4 h-4 text-blue-600" /> From your pins
              </h2>
              <div>
                {pinnedCards.map((ev) => (
                  <EventCard key={ev.event_id} event={ev} />
                ))}
              </div>
            </section>
          )}

          <section data-testid="np-for-you-other-feed">
            <h2 className="text-xl font-bold text-slate-900 mb-3">
              {pinnedCards.length > 0 ? "Also on the network" : "Latest headlines"}
            </h2>
            <div>
              {otherCards.map((ev) => (
                <EventCard key={ev.event_id} event={ev} />
              ))}
              {otherCards.length === 0 && (
                <div className="text-sm text-slate-500 py-8 text-center">
                  No stories yet — check back shortly.
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
